import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { ResponseAPI } from '../models/HttpBase/response-api';
import {
  ExposicionCatalogoResponse,
  PromedioExposicionAgrupadoResponse,
  PromedioExposicionRequest
} from '../models/exposicion/exposicion-catalogo.dto';
import {
  ExposicionFiltroRequest,
  ExposicionListadoItem
} from '../models/exposicion/exposicion-listado.dto';
import { HttpClientService } from './http-client.service';

interface ApiCacheEntry<T> {
  response: ResponseAPI<T>;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExposicionCatalogoApiService {
  private readonly exposicionBasePath = 'Exposicion';
  private readonly exposicionesTtlMs = 5 * 60 * 1000;
  private readonly catalogoTtlMs = 2 * 60 * 1000;

  private readonly exposicionesCache = new Map<string, ApiCacheEntry<ExposicionListadoItem[]>>();
  private readonly exposicionesInFlight = new Map<string, Observable<ResponseAPI<ExposicionListadoItem[]>>>();

  private readonly catalogoCache = new Map<number, ApiCacheEntry<ExposicionCatalogoResponse>>();
  private readonly catalogoInFlight = new Map<number, Observable<ResponseAPI<ExposicionCatalogoResponse>>>();

  constructor(private readonly httpClientService: HttpClientService) {}

  public getExposiciones(
    filtro: ExposicionFiltroRequest = {},
    forceRefresh = false
  ): Observable<ResponseAPI<ExposicionListadoItem[]>> {
    const key = this.buildFiltroKey(filtro);

    if (!forceRefresh) {
      const cached = this.getCacheValue(this.exposicionesCache, key);
      if (cached) {
        return of(cached);
      }
    }

    const inFlight = this.exposicionesInFlight.get(key);
    if (inFlight) {
      return inFlight;
    }

    const request$ = this.httpClientService
      .postAnonymous<ExposicionListadoItem[]>(`${this.exposicionBasePath}/getExposiciones`, filtro)
      .pipe(
        tap((response) => {
          if (response.Success) {
            this.exposicionesCache.set(key, {
              response,
              expiresAt: Date.now() + this.exposicionesTtlMs
            });
          }
        }),
        finalize(() => {
          this.exposicionesInFlight.delete(key);
        }),
        shareReplay(1)
      );

    this.exposicionesInFlight.set(key, request$);
    return request$;
  }

  public getCatalogo(
    exposicionId: number,
    forceRefresh = false
  ): Observable<ResponseAPI<ExposicionCatalogoResponse>> {
    return this.getCatalogoInternal(exposicionId, forceRefresh, false);
  }

  public getCatalogoSilent(
    exposicionId: number,
    forceRefresh = false
  ): Observable<ResponseAPI<ExposicionCatalogoResponse>> {
    return this.getCatalogoInternal(exposicionId, forceRefresh, true);
  }

  private getCatalogoInternal(
    exposicionId: number,
    forceRefresh: boolean,
    silent: boolean
  ): Observable<ResponseAPI<ExposicionCatalogoResponse>> {
    if (!forceRefresh) {
      const cached = this.getCacheValue(this.catalogoCache, exposicionId);
      if (cached) {
        return of(cached);
      }
    }

    const inFlight = this.catalogoInFlight.get(exposicionId);
    if (inFlight) {
      return inFlight;
    }

    const path = `${this.exposicionBasePath}/getCatalogo/${exposicionId}`;
    const source$: Observable<ResponseAPI<ExposicionCatalogoResponse>> = silent
      ? this.httpClientService
          .getSilent<ResponseAPI<ExposicionCatalogoResponse>>(path)
          .pipe(map((response) => this.hydrateResponse<ExposicionCatalogoResponse>(response)))
      : this.httpClientService.get<ExposicionCatalogoResponse>(path);

    const request$ = source$
      .pipe(
        tap((response) => {
          if (response.Success) {
            this.catalogoCache.set(exposicionId, {
              response,
              expiresAt: Date.now() + this.catalogoTtlMs
            });
          }
        }),
        finalize(() => {
          this.catalogoInFlight.delete(exposicionId);
        }),
        shareReplay(1)
      );

    this.catalogoInFlight.set(exposicionId, request$);
    return request$;
  }

  public invalidateExposiciones(filtro?: ExposicionFiltroRequest): void {
    if (!filtro) {
      this.exposicionesCache.clear();
      this.exposicionesInFlight.clear();
      return;
    }

    const key = this.buildFiltroKey(filtro);
    this.exposicionesCache.delete(key);
    this.exposicionesInFlight.delete(key);
  }

  public invalidateCatalogo(exposicionId?: number): void {
    if (!exposicionId || exposicionId <= 0) {
      this.catalogoCache.clear();
      this.catalogoInFlight.clear();
      return;
    }

    this.catalogoCache.delete(exposicionId);
    this.catalogoInFlight.delete(exposicionId);
  }

  public clearAllCache(): void {
    this.invalidateExposiciones();
    this.invalidateCatalogo();
  }

  private getCacheValue<K, T>(cache: Map<K, ApiCacheEntry<T>>, key: K): ResponseAPI<T> | null {
    const entry = cache.get(key);
    if (!entry) {
      return null;
    }

    if (entry.expiresAt <= Date.now()) {
      cache.delete(key);
      return null;
    }

    return entry.response;
  }

  private buildFiltroKey(filtro: ExposicionFiltroRequest): string {
    const source = (filtro ?? {}) as Record<string, unknown>;
    const normalized: Record<string, unknown> = {};

    for (const key of Object.keys(source).sort()) {
      const value = source[key];
      if (value === undefined || value === null || value === '') {
        continue;
      }

      normalized[key] = value;
    }

    return JSON.stringify(normalized);
  }

  private hydrateResponse<T>(response: unknown): ResponseAPI<T> {
    return Object.assign(new ResponseAPI<T>(), response);
  }

  public getPromedios(
    exposicionID: number,
    sexo: number | null = null
  ): Observable<ResponseAPI<PromedioExposicionAgrupadoResponse[]>> {
    const req: PromedioExposicionRequest = {
      ExposicionesID: exposicionID > 0 ? [exposicionID] : [],
      Sexo: sexo,
    };
    const path = `${this.exposicionBasePath}/promediosExposiciones`;
    return this.httpClientService.post<PromedioExposicionAgrupadoResponse[]>(path, req);
  }
}
